import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, computed } from 'vue';


const __default__ = { name: 'ReportVentasPorMedio' }


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    data: {},
    loader: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

// Dark mode
const isDarkMode = ref(
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false
);
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', e => (isDarkMode.value = e.matches));
}

// Helpers de color
function hexToHsl(hex: string) {
  let r = 0, g = 0, b = 0;
  const c = hex.replace('#','');
  if (c.length === 3) {
    r = parseInt(c[0] + c[0], 16);
    g = parseInt(c[1] + c[1], 16);
    b = parseInt(c[2] + c[2], 16);
  } else {
    r = parseInt(c.substring(0,2), 16);
    g = parseInt(c.substring(2,4), 16);
    b = parseInt(c.substring(4,6), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h = 0, s = 0, l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h, s, l };
}
function hslToHex(h: number, s: number, l: number) {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l; // achromático
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  const toHex = (x: number) => {
    const v = Math.round(x * 255).toString(16).padStart(2, '0');
    return v;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function buildScale(baseHex: string, n: number) {
  // Genera n tonos variando la luminosidad (l) y un poco la saturación (s)
  const { h, s, l } = hexToHsl(baseHex);
  const colors: string[] = [];
  // rango de l entre 0.32 y 0.72 para buena legibilidad
  const lMin = 0.32, lMax = 0.72;
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const li = lMin + (lMax - lMin) * (1 - t);     // de más oscuro a más claro
    const si = Math.min(1, Math.max(0.35, s * (0.85 + 0.2 * (1 - t))));
    colors.push(hslToHex(h, si, li));
  }
  return colors;
}

// Hostname → paleta
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isDominiomedios = hostname === 'crm.dominiomedios.com';

const dominiomediosPaletteBase = ['#88102e', '#b93d50', '#d97a8b', '#f2c2ca'];
const blueBase = '#2563eb';

const chartSeries = computed(() =>
  (props.data ?? []).map(item => parseFloat(item.total_vendido) || 0)
);

const chartLabels = computed(() =>
  (props.data ?? []).map(item => item.medio ?? 'Sin nombre')
);

// Paleta final del gráfico (del tamaño exacto de la serie)
const chartColors = computed(() => {
  const n = Math.max(1, chartSeries.value.length);
  if (isDominiomedios) {
    // Repite/recorta la paleta roja fija al tamaño n
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      out.push(dominiomediosPaletteBase[i % dominiomediosPaletteBase.length]);
    }
    return out;
  }
  // Construye escala azul desde #2563eb
  return buildScale(blueBase, n);
});

const chartOptions = computed(() => ({
  labels: chartLabels.value,
  chart: {
    type: 'pie',
    foreColor: isDarkMode.value ? '#fff' : '#333',
    toolbar: { show: false }
  },
  dataLabels: {
    enabled: true,
    style: {
      fontSize: '18px',
      fontWeight: 'bold',
      colors: [isDarkMode.value ? '#fff' : 'white'],
      textShadow: '2px 2px 2px rgba(0,0,0,0.20)'
    },
    formatter: (val: number) => `${val.toFixed(1)}%`
  },
  stroke: { width: 0 },
  legend: {
    position: 'right',
    labels: {
      colors: isDarkMode.value ? '#fff' : '#333',
      useSeriesColors: false,
    },
    markers: { strokeWidth: 0 }
  },
  tooltip: {
    theme: isDarkMode.value ? 'dark' : 'light',
    y: {
      formatter: (val: number) =>
        Number(val).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    }
  },
  colors: chartColors.value, // ← paleta dinámica
  responsive: [
    {
      breakpoint: 600,
      options: {
        chart: { width: '100%' },
        legend: { position: 'bottom' }
      }
    }
  ]
}));

const loader = computed(() => props.loader ?? false);

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_apexchart = _resolveComponent("apexchart")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    flat: "",
    class: "py-12 px-3"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, { class: "text-center" }, {
        default: _withCtx(() => _cache[0] || (_cache[0] = [
          _createTextVNode("Ventas por Medio")
        ])),
        _: 1,
        __: [0]
      }),
      (loader.value)
        ? (_openBlock(), _createBlock(_component_v_progress_linear, {
            key: 0,
            indeterminate: "",
            color: "primary"
          }))
        : (_openBlock(), _createBlock(_component_apexchart, {
            key: 1,
            type: "pie",
            height: "336",
            options: chartOptions.value,
            series: chartSeries.value
          }, null, 8, ["options", "series"]))
    ]),
    _: 1
  }))
}
}

})